import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import Link from '../../Link';
import Loader from '../../Loader';
import {
  fetchAdminInspectionReservation, fetchInspectionComparison, fetchDispute
} from '../../../actions/Inspection/inspection';
import { globalStatusKey, globalStatusTone } from '../../../helpers/inspectionPresentation';
import InspectionBanner from '../../Inspection/InspectionBanner/InspectionBanner';
import InspectionStatusBadge from '../../Inspection/InspectionStatusBadge/InspectionStatusBadge';
import InspectionDetails from '../../Inspection/InspectionDetails/InspectionDetails';
import InspectionTimeline from './InspectionTimeline';
import PhotoComparison from './PhotoComparison';
import ins from '../../Inspection/Inspection.css';
import s from './InspectionAdmin.css';

// Check-in / check-out part of the reservation detail (R13, R14): global status, timeline, both records (read-only), departure
// photos next to damage photos with zoom, the comments of both parties and the shortcuts to the dispute and to the deposit.
// Everything is read-only here: the admin never edits a check-in or a check-out.
export function AdminInspectionSection({ reservationId }) {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    (async () => {
      const [reservation, comparison, dispute] = await Promise.all([
        dispatch(fetchAdminInspectionReservation(reservationId)),
        dispatch(fetchInspectionComparison(reservationId)),
        dispatch(fetchDispute(reservationId))
      ]);
      if (!alive) return;
      const ok = comparison && comparison.status === 'success';
      setState({
        loading: false,
        failed: !ok || !reservation || !reservation.id,
        errorMessage: ok ? null : comparison && comparison.errorMessage,
        reservation,
        comparison: ok ? comparison : null,
        dispute: dispute && dispute.status === 'success' ? dispute.dispute : null
      });
    })();
    return () => { alive = false; };
  }, [reservationId]);

  if (state.loading) return <Loader type="text" />;
  if (state.failed) return <InspectionBanner tone="danger" title={state.errorMessage || formatMessage(messages.inspLoadFailed)} />;

  const { reservation, comparison, dispute } = state;
  const { checkin, checkout } = comparison;
  const statusTone = globalStatusTone(comparison.globalStatus);
  const damagePhotos = checkout && checkout.hasDamage ? checkout.photos : [];
  const comments = [
    checkin && checkin.comment ? { role: 'inspRoleHost', text: checkin.comment, at: checkin.updatedAt, title: 'inspCheckinTitle' } : null,
    checkout && checkout.comment ? { role: checkout.authorRole === 'host' ? 'inspRoleHost' : 'inspRoleGuest', text: checkout.comment, at: checkout.updatedAt, title: 'inspCheckoutTitle' } : null,
    checkout && checkout.hasDamage && checkout.damageDescription ? { role: checkout.authorRole === 'host' ? 'inspRoleHost' : 'inspRoleGuest', text: checkout.damageDescription, at: checkout.updatedAt, title: 'inspDamageDescriptionTitle' } : null
  ].filter(Boolean);
  const when = (value) => (value ? new Date(value).toLocaleString(locale) : '');

  return (
    <div>
      <h2 className={ins.title} id="inspection">{formatMessage(messages.inspAdmSectionTitle)}</h2>
      <p className={ins.subtitle}>
        <InspectionStatusBadge entry={{ labelKey: globalStatusKey(comparison.globalStatus), tone: statusTone, icon: statusTone === 'danger' ? 'alert' : statusTone === 'success' ? 'ok' : 'clock' }} />
        {' '}
        {reservation.hasDispute && <Link to={`/siteadmin/reservation-dispute/${reservation.id}`} className={ins.entryLink}>{formatMessage(messages.inspAdmSeeDispute)}</Link>}
        {' '}
        <Link to={`/siteadmin/viewreservation/${reservation.id}/security-deposit`} className={ins.entryLink}>{formatMessage(messages.inspAdmSeeDeposit)}</Link>
      </p>

      <section className={ins.card}><InspectionTimeline reservation={reservation} checkin={checkin} checkout={checkout} dispute={dispute} /></section>

      <section className={ins.card}>
        <h3 className={ins.cardTitle}>{formatMessage(messages.inspAdmComparisonTitle)}</h3>
        <ul className={s.metrics}>
          <li className={s.metric}>{formatMessage(messages.inspAdmKmDriven)}: <span className={s.metricValue}>{comparison.kmDriven === null || comparison.kmDriven === undefined ? '-' : `${comparison.kmDriven} ${formatMessage(messages.inspKm)}`}</span></li>
          <li className={s.metric}>{formatMessage(messages.inspAdmFuelDelta)}: <span className={s.metricValue}>{comparison.fuelDelta === null || comparison.fuelDelta === undefined ? '-' : comparison.fuelDelta}</span></li>
          <li className={s.metric}>{formatMessage(messages.inspAdmDamagePhotos)}: <span className={s.metricValue}>{comparison.newDamagePhotos || 0}</span></li>
        </ul>
        {(comparison.alerts || []).map((alert) => (
          <InspectionBanner key={alert.code} tone="warning">{formatMessage(messages.inspAdmAlert, { code: alert.code, value: alert.value === null || alert.value === undefined ? '' : String(alert.value) })}</InspectionBanner>
        ))}
        <PhotoComparison departure={checkin ? checkin.photos : []} damage={damagePhotos} />
      </section>

      <div className={ins.grid2} style={{ marginBottom: 16 }}>
        <section className={ins.card} id="checkin">
          <h3 className={ins.cardTitle}>{formatMessage(messages.inspCheckinTitle)} <InspectionStatusBadge status={checkin ? checkin.status : 'not_done'} /></h3>
          {checkin
            ? <><p className={ins.cardHint}>{formatMessage(messages.inspAdmValidations, { host: when(checkin.hostValidatedAt) || '-', guest: when(checkin.guestValidatedAt) || '-', version: checkin.version })}</p><InspectionDetails inspection={checkin} type="checkin" max={8} /></>
            : <p className={ins.cardHint}>{formatMessage(messages.inspAdmNotDone)}</p>}
        </section>
        <section className={ins.card} id="checkout">
          <h3 className={ins.cardTitle}>{formatMessage(messages.inspCheckoutTitle)} <InspectionStatusBadge status={checkout ? checkout.status : 'not_done'} /></h3>
          {checkout
            ? <><p className={ins.cardHint}>{formatMessage(messages.inspAdmValidations, { host: when(checkout.hostValidatedAt) || '-', guest: when(checkout.guestValidatedAt) || '-', version: checkout.version })}</p><InspectionDetails inspection={checkout} type="checkout" max={8} /></>
            : <p className={ins.cardHint}>{formatMessage(messages.inspAdmNotDone)}</p>}
        </section>
      </div>

      <section className={ins.card}>
        <h3 className={ins.cardTitle}>{formatMessage(messages.inspAdmCommentsTitle)}</h3>
        {comments.length === 0 && <p className={ins.cardHint}>{formatMessage(messages.inspAdmNoComments)}</p>}
        <ul className={ins.thread}>
          {comments.map((item) => (
            <li key={`${item.title}-${item.role}`} className={ins.statement}>
              <p className={ins.statementHead}><span>{formatMessage(messages[item.role])} - {formatMessage(messages[item.title])}</span><span>{when(item.at)}</span></p>
              <p className={ins.statementBody}>{item.text}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

AdminInspectionSection.propTypes = { reservationId: PropTypes.number.isRequired };

export default withStyles(ins, s)(AdminInspectionSection);
